import { notification } from "antd";
import React from "react";
import { globalAppConfig } from "../../../../config";
import GlobalStoreContext from "../../../../context/GlobalStoreContext";
import { Team } from "../../../../models/Team";
import PlotAPI from "../../../../plotAPI";
import CreateFields from "./createFields";
import SearchOrCreate from "./searchOrCreate";

interface Props {
    visible: boolean;
    onCancel: () => void;
    onSelect: (team_id: number) => void;
    vendorMode?: boolean;
}

const AddManual: React.FC<Props> = (props) => {
    const context = React.useContext(GlobalStoreContext);

    const [selectedTeam, setSelectedTeam] = React.useState<Team | null>(null);

    const cleanup = (team: any) => {
        // Clear out any params in the URL in case we came here from the request to join email
        window.history.replaceState({}, "", window.location.pathname);

        props.onSelect(team.id);

        if (props.vendorMode) {
            context.refreshVendorsByProject();
        } else {
            context.refreshTeamsByProject();
        }

        setSelectedTeam(null);

        notification.success({
            message: "Company Added",
            description: `"${team.team_name}" has been added to the jobsite`,
            duration: globalAppConfig.NOTIFICATION_DURATION,
        });

        props.onCancel();
    };

    const inviteUserToTeamIfNeeded = (team: Team): Promise<any> => {
        const urlParams = new URLSearchParams(window.location.search);

        const inviteContact = urlParams.get("invite_contact");

        if (!inviteContact || inviteContact.length === 0) {
            return Promise.resolve();
        }

        if (!team.subdomain || team.subdomain.length === 0) {
            return Promise.reject("Team subdomain is missing");
        }

        return PlotAPI.inviteToTeam(
            [{ contact_info: inviteContact, permission: "is_member" }],
            team.subdomain,
            context.currentProjectInfo.unique_token
        ).then(() => cleanup(team));
    };

    const inviteTeamToProject = (team: Team) => {
        return PlotAPI.addTeam(
            context.currentProjectInfo.unique_token,
            team.id,
            props.vendorMode ? "vendor" : "member"
        );
    };

    const onInviteExisting = () => {
        if (!selectedTeam) {
            return;
        }

        inviteTeamToProject(selectedTeam)
            .then(() => inviteUserToTeamIfNeeded(selectedTeam))
            .then(() => cleanup(selectedTeam));
    };

    const createTeam = (team: Team): Promise<any> => {
        return PlotAPI.postTeam(team.toApiObject());
    };

    const onInviteNew = () => {
        if (!selectedTeam) {
            return;
        }

        createTeam(selectedTeam).then((team) =>
            inviteTeamToProject(team)
                .then(() => inviteUserToTeamIfNeeded(team))
                .then(() => cleanup(team))
        );
    };

    const renderAddExisting = () => {
        if (!selectedTeam) {
            return <></>;
        }

        return (
            <div className="flex flex-col gap-5 items-center">
                <div className="w-full">
                    <button className="plot-button secondary-button button w-1/2" onClick={props.onCancel}>
                        Cancel
                    </button>
                    <button className="plot-button primary-button button w-1/2" onClick={onInviteExisting}>
                        Add
                    </button>
                </div>
            </div>
        );
    };

    const renderAddNew = () => {
        if (!selectedTeam) {
            return <></>;
        }

        return (
            <div className="flex flex-col gap-5 items-center">
                <CreateFields
                    vendorMode={props.vendorMode}
                    selectedTeam={selectedTeam}
                    setSelectedTeam={setSelectedTeam}
                />

                <div className="w-full">
                    <button className="plot-button secondary-button button w-1/2" onClick={props.onCancel}>
                        Cancel
                    </button>
                    <button
                        className="plot-button primary-button button w-1/2"
                        disabled={
                            !selectedTeam.teamName ||
                            selectedTeam.teamName.length < 3 ||
                            (context.currentProjectInfo.require_vendor_address && !selectedTeam.address1)
                        }
                        onClick={onInviteNew}
                    >
                        Create
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="flex flex-col gap-5">
            <p className="claimJoinLabel">
                Use the field below to search for an existing {props.vendorMode ? "vendor" : "company"} or create new:
            </p>

            <SearchOrCreate visible={props.visible} setSelectedTeam={setSelectedTeam} vendorMode={props.vendorMode} />

            {/* Add an existing Company to the jobsite as the selected mode */}
            {selectedTeam && selectedTeam.id !== -1 && renderAddExisting()}

            {/* Create a new Company for the jobsite as the selected mode */}
            {selectedTeam && selectedTeam.id === -1 && renderAddNew()}
        </div>
    );
};

export default AddManual;
